// G-New Features — pure, READ-ONLY data layer for the isolated Golf Genius
// staging area. Composes cart-sign / scorecard data from existing tournament
// data via existing READ helpers only (no data.js writers, no DB writes).
//
// resolveStartingHole / resolveTeamDay are COPIED from ScoreApp.jsx on purpose:
// GG must never import or touch the live scoring path. resolveTeeTime is new.
// Pure → unit-testable under `node --test`.
import { parsFor, strokeIndexFor, findOpponent, getPlayStyle } from '../../lib/scoring.js'
import { strokesOnHole } from '../../lib/handicapCalc.js'
import CLUB from '../../config/club.js'

// ── Copied resolvers (self-contained; do not import ScoreApp) ───────────────
function resolveStartingHole(tournament, flightId, teamId, team) {
  const fromInfo = Number(team?.info?.startingHole)
  if (Number.isFinite(fromInfo) && fromInfo >= 1 && fromInfo <= 18) return fromInfo
  const teeTimes = tournament?.teeTimes
  if (teeTimes && typeof teeTimes === 'object') {
    for (const dayKey of Object.keys(teeTimes)) {
      const waves = teeTimes[dayKey]?.waves
      if (!waves || typeof waves !== 'object') continue
      for (const waveKey of Object.keys(waves)) {
        const raw = waves[waveKey]?.assignments?.[teamId]
        if (raw == null || raw === '') continue
        const m = String(raw).match(/\d+/)
        if (!m) continue
        const n = Number(m[0])
        if (Number.isFinite(n) && n >= 1 && n <= 18) return n
      }
    }
  }
  return 1
}

function resolveTeamDay(tournament, teamId) {
  const teeTimes = tournament?.teeTimes
  if (teeTimes && typeof teeTimes === 'object') {
    for (const dayKey of Object.keys(teeTimes)) {
      const waves = teeTimes[dayKey]?.waves
      if (!waves || typeof waves !== 'object') continue
      for (const waveKey of Object.keys(waves)) {
        const a = waves[waveKey]?.assignments
        if (a && a[teamId] != null && a[teamId] !== '') {
          const n = Number(dayKey)
          return Number.isFinite(n) && n >= 1 ? n : 1
        }
      }
    }
  }
  return 1
}

// Tee time display string: per-flight model (teeTimes/{day}/{flightId} = "8:00
// AM") or the shotgun wave startTime whose flights/assignments include this
// team. null when none.
function resolveTeeTime(tournament, flightId, teamId, day) {
  const dayNode = tournament?.teeTimes?.[day]
  if (!dayNode || typeof dayNode !== 'object') return null
  const perFlight = dayNode[flightId]
  if (typeof perFlight === 'string' && perFlight.trim()) return perFlight
  const waves = dayNode.waves
  if (waves && typeof waves === 'object') {
    for (const w of Object.values(waves)) {
      const inFlight = !!w?.flights?.[flightId]
      const inAssign = w?.assignments?.[teamId] != null && w.assignments[teamId] !== ''
      if ((inFlight || inAssign) && w?.startTime) return w.startTime
    }
  }
  return null
}

function buildPlayer(name, playingHandicap, strokeIndex) {
  const ph = Number.isFinite(Number(playingHandicap)) ? Number(playingHandicap) : null
  const strokesByHole = Array.from({ length: 18 }, (_, h) => (ph == null ? 0 : strokesOnHole(ph, h, strokeIndex)))
  return { name, playingHandicap: ph, strokesByHole }
}

function buildTeamPlayers(tinfo, strokeIndex) {
  const names = Array.isArray(tinfo?.players) ? tinfo.players : []
  const phs = Array.isArray(tinfo?.playingHandicaps) ? tinfo.playingHandicaps : []
  return names.map((nm, i) => buildPlayer(typeof nm === 'string' ? nm : (nm?.name || ''), phs[i], strokeIndex))
}

// buildGroups(tournament) → { meta, groups }. One group per team; opponent is
// populated only for an explicit match pairing (info.opponentId) or a match-
// play event — otherwise it's just the group.
export function buildGroups(tournament) {
  const pars = parsFor(tournament)
  const strokeIndex = strokeIndexFor(tournament)
  const info = tournament?.info || {}
  const isMatch = getPlayStyle(tournament, null) === 'match'
  const meta = {
    tournamentName: info.name || 'Tournament',
    courseName: info.course || CLUB.course?.name || '',
    date: info.date || '',
    pars,
    strokeIndex,
    isMatch,
    clubName: CLUB.name,
  }
  const groups = []
  for (const [flightId, flight] of Object.entries(tournament?.flights || {})) {
    const flightName = flight?.info?.name || flightId
    const teams = flight?.teams || {}
    for (const [teamId, team] of Object.entries(teams)) {
      const tinfo = team?.info || {}
      const day = resolveTeamDay(tournament, teamId)
      let opponent = null
      if (tinfo.opponentId && teams[tinfo.opponentId]) {
        const o = teams[tinfo.opponentId]
        opponent = { teamId: tinfo.opponentId, teamName: o.info?.name || tinfo.opponentId, players: buildTeamPlayers(o.info || {}, strokeIndex) }
      } else if (isMatch) {
        const oppRaw = findOpponent(flight, teamId)
        if (oppRaw && oppRaw.id !== teamId) {
          opponent = { teamId: oppRaw.id, teamName: oppRaw.info?.name || oppRaw.id, players: buildTeamPlayers(oppRaw.info || {}, strokeIndex) }
        }
      }
      groups.push({
        flightId,
        flightName,
        teamId,
        teamName: tinfo.name || teamId,
        day,
        startingHole: resolveStartingHole(tournament, flightId, teamId, team),
        teeTime: resolveTeeTime(tournament, flightId, teamId, day),
        players: buildTeamPlayers(tinfo, strokeIndex),
        opponent,
      })
    }
  }
  return { meta, groups }
}
